import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/elements/button';
import { Search, Calendar, ExportIcon } from '../../assets/icons';
import '../../assets/css/admin/orders.css';
import apiConfig from '../../config/api_config';
import LoadingSpinner from '../../components/elements/LoadingSpinner';

const AdminOrders = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        status: 'all',
        timeframe: 'all',
        vendor: 'all'
    });

    const [orders, setOrders] = useState([]);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiConfig.get('/orders/');
            if (response?.data?.data) {
                setOrders(response.data.data);
            } else {
                setOrders([]);
            }
        } catch (err) {
            console.error('Failed to fetch orders:', err);
            setError('Failed to load orders');
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    // Calculate stats dynamically
    const stats = useMemo(() => {
        const totalOrders = orders.length;
        const pendingOrders = orders.filter(o => o.status === 'pending').length;
        const completedOrders = orders.filter(o => o.status === 'completed' || o.status === 'delivered').length;
        const cancelledOrders = orders.filter(o => o.status === 'cancelled').length;
        const revenue = orders
            .filter(o => o.status === 'completed' || o.status === 'delivered')
            .reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);
        
        return { totalOrders, pendingOrders, completedOrders, cancelledOrders, revenue };
    }, [orders]);

    // Filter orders
    const filteredOrders = useMemo(() => {
        return orders.filter(order => {
            const searchMatch = 
                String(order.id).includes(searchTerm) ||
                order.user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.shipping_name?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const statusMatch = filters.status === 'all' || order.status === filters.status;
            
            return searchMatch && statusMatch;
        });
    }, [orders, searchTerm, filters]);

    if (loading) {
        return (
            <div className="admin-orders">
                <div className="loading-state">
                    <LoadingSpinner />
                    <p>Loading orders...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-orders">
                <div className="error-state">
                    <p>{error}</p>
                    <Button text="Retry" onClick={fetchOrders} />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-orders">
            <div className="page-header">
                <div className="header-content">
                    <h1>Order Management</h1>
                    <p>Monitor and manage customer orders</p>
                </div>
                <div className="header-actions">
                    <Button
                        icon={<img src={ExportIcon} alt="" />}
                        text="Export Report"
                        variant="outline"
                    />
                    <Button
                        icon={<img src={Calendar} alt="" />}
                        text="Schedule Delivery"
                        variant="primary"
                    />
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <h3>Total Orders</h3>
                    <div className="stat-value">{stats.totalOrders}</div>
                </div>
                <div className="stat-card">
                    <h3>Pending</h3>
                    <div className="stat-value warning">{stats.pendingOrders}</div>
                </div>
                <div className="stat-card">
                    <h3>Completed</h3>
                    <div className="stat-value success">{stats.completedOrders}</div>
                </div>
                <div className="stat-card">
                    <h3>Total Revenue</h3>
                    <div className="stat-value">₦{stats.revenue.toLocaleString()}</div>
                </div>
            </div>

            <div className="filters-section">
                <div className="search-bar">
                    <img src={Search} alt="" />
                    <input
                        type="text"
                        placeholder="Search orders by ID, customer, or vendor..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filters">
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                    </select>

                    <select
                        value={filters.timeframe}
                        onChange={(e) => setFilters({...filters, timeframe: e.target.value})}
                    >
                        <option value="all">All Time</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                    </select>

                    <select
                        value={filters.vendor}
                        onChange={(e) => setFilters({...filters, vendor: e.target.value})}
                    >
                        <option value="all">All Vendors</option>
                        <option value="aqua">AquaSupplies Ltd</option>
                        <option value="fish">FishTech Store</option>
                    </select>
                </div>
            </div>

            <div className="orders-list">
                {filteredOrders.length === 0 ? (
                    <div className="empty-state">
                        <p>No orders found</p>
                    </div>
                ) : (
                    filteredOrders.map(order => (
                    <div key={order.id} className="order-card">
                        <div className="order-header">
                            <div className="order-meta">
                                <h3>Order #ORD{String(order.id).padStart(3, '0')}</h3>
                                <span className={`status ${order.status}`}>
                                    {order.status}
                                </span>
                            </div>
                            <div className="payment-badge">
                                <span className={`payment-status ${order.payment_status || 'pending'}`}>
                                    {order.payment_status || 'pending'}
                                </span>
                            </div>
                        </div>

                        <div className="customer-info">
                            <div className="info-group">
                                <label>Customer</label>
                                <span>{order.user?.first_name ? `${order.user.first_name} ${order.user.last_name}` : order.shipping_name || 'N/A'}</span>
                            </div>
                            <div className="info-group">
                                <label>Email</label>
                                <span>{order.user?.email || order.shipping_email || 'N/A'}</span>
                            </div>
                            <div className="info-group">
                                <label>Location</label>
                                <span>{order.shipping_address || order.shipping_city || 'N/A'}</span>
                            </div>
                        </div>

                        {order.items && order.items.length > 0 && (
                            <div className="order-items">
                                {order.items.map((item, index) => (
                                    <div key={index} className="item">
                                        {item.product?.image && <img src={item.product.image} alt={item.product?.name} />}
                                        <div className="item-details">
                                            <h4>{item.product?.name || 'Product'}</h4>
                                            <div className="item-meta">
                                                <span>Qty: {item.quantity}</span>
                                                <span>₦{parseFloat(item.price || 0).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="order-summary">
                            <div className="dates">
                                <div className="date-group">
                                    <label>Order Date</label>
                                    <span>{order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}</span>
                                </div>
                            </div>
                            <div className="total">
                                <label>Total Amount</label>
                                <span>₦{parseFloat(order.total || 0).toLocaleString()}</span>
                            </div>
                        </div>

                        <div className="card-actions">
                            <Link to={`/admin/orders/${order.id}`}>
                                <Button
                                    text="View Details"
                                    variant="outline"
                                />
                            </Link>
                            <Button
                                text="Update Status"
                                variant="primary"
                            />
                            {order.status === 'pending' && (
                                <Button
                                    text="Cancel Order"
                                    variant="error"
                                />
                            )}
                        </div>
                    </div>
                )))}
            </div>
        </div>
    );
};

export default AdminOrders;